import { useState } from 'react';
import { CreditCard, Link2, Loader2, LogIn, PlusCircle } from 'lucide-react';
import type { ManagedStripeEntity, StripeConnectScope } from '../../lib/stripeConnect';
import {
  linkStripeConnectAccount,
  startOrganizationStripeConnect,
  startOrganizationStripeConnectOAuth,
  startTeamStripeConnect,
  startTeamStripeConnectOAuth,
} from '../../lib/stripeConnect';

export type StripeConnectMethod = 'existing' | 'new' | 'podium';

interface StripeConnectMethodPickerProps {
  scope: StripeConnectScope;
  entityId: string;
  linkableSource?: (ManagedStripeEntity & { account: { stripe_account_id: string | null } }) | null;
  onLinked?: () => void;
  disabled?: boolean;
  compact?: boolean;
}

export function StripeConnectMethodPicker({
  scope,
  entityId,
  linkableSource,
  onLinked,
  disabled = false,
  compact = false,
}: StripeConnectMethodPickerProps) {
  const [loading, setLoading] = useState<StripeConnectMethod | null>(null);
  const [error, setError] = useState('');

  const startNew = async () => {
    const url =
      scope === 'team'
        ? await startTeamStripeConnect(entityId)
        : await startOrganizationStripeConnect(entityId);
    window.location.href = url;
  };

  const startExisting = async () => {
    const url =
      scope === 'team'
        ? await startTeamStripeConnectOAuth(entityId)
        : await startOrganizationStripeConnectOAuth(entityId);
    window.location.href = url;
  };

  const linkFromPodium = async () => {
    if (!linkableSource) return;
    await linkStripeConnectAccount(
      linkableSource.scope,
      linkableSource.id,
      scope,
      entityId,
    );
    onLinked?.();
  };

  const handleMethod = async (method: StripeConnectMethod) => {
    setLoading(method);
    setError('');
    try {
      if (method === 'new') await startNew();
      else if (method === 'existing') await startExisting();
      else await linkFromPodium();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not connect Stripe');
      setLoading(null);
    }
  };

  const buttonClass = compact
    ? 'w-full text-left p-3 rounded-xl border-2 border-gray-200 dark:border-slate-700 hover:border-[#635BFF] dark:hover:border-[#635BFF] hover:bg-indigo-50/50 dark:hover:bg-indigo-500/10 transition-colors disabled:opacity-50'
    : 'w-full text-left p-4 rounded-xl border-2 border-gray-200 dark:border-slate-700 hover:border-[#635BFF] dark:hover:border-[#635BFF] hover:bg-indigo-50/50 dark:hover:bg-indigo-500/10 transition-colors disabled:opacity-50';

  return (
    <div className="space-y-3">
      <button
        type="button"
        disabled={disabled || !!loading}
        onClick={() => handleMethod('existing')}
        className={buttonClass}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#635BFF]/10 flex items-center justify-center flex-shrink-0">
            {loading === 'existing' ? (
              <Loader2 className="w-5 h-5 text-[#635BFF] animate-spin" />
            ) : (
              <LogIn className="w-5 h-5 text-[#635BFF]" />
            )}
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">
              Sign in with your Stripe account
            </p>
            <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
              Use the Stripe login for this team or organization — not the Podium HQ platform account.
              Payouts go to the bank account on that Stripe account.
            </p>
          </div>
        </div>
      </button>

      {linkableSource && (
        <button
          type="button"
          disabled={disabled || !!loading}
          onClick={() => handleMethod('podium')}
          className={buttonClass}
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-100 dark:bg-teal-500/20 flex items-center justify-center flex-shrink-0">
              {loading === 'podium' ? (
                <Loader2 className="w-5 h-5 text-teal-700 dark:text-teal-300 animate-spin" />
              ) : (
                <Link2 className="w-5 h-5 text-teal-700 dark:text-teal-300" />
              )}
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-white">
                Use Stripe from {linkableSource.name}
              </p>
              <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                Reuse the Stripe account already connected to another team or organization you manage.
              </p>
            </div>
          </div>
        </button>
      )}

      <button
        type="button"
        disabled={disabled || !!loading}
        onClick={() => handleMethod('new')}
        className={buttonClass}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
            {loading === 'new' ? (
              <Loader2 className="w-5 h-5 text-gray-600 dark:text-slate-300 animate-spin" />
            ) : (
              <PlusCircle className="w-5 h-5 text-gray-600 dark:text-slate-300" />
            )}
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">Create a new Stripe account</p>
            <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
              Set up a fresh Stripe account for payouts through Podium HQ.
            </p>
          </div>
        </div>
      </button>

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {!compact && (
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-500 pt-1">
          <CreditCard className="w-3.5 h-3.5" />
          <span>All options are handled securely by Stripe.</span>
        </div>
      )}
    </div>
  );
}
